import { isNil } from 'lodash';
import { atom, atomFamily, DefaultValue, selector, selectorFamily } from 'recoil';
import type { CombinePortfoliosOrganizeOptionEnum, PrivatePortfolioNode } from 'venn-api';
import type { AnalysisSubject } from 'venn-utils';
import { recoilHistoryEffect } from '../../effects';
import { resetOnStudioReset } from '../../effects/signalEffects';
import type { AllocatorConfig, PrivateAllocatorConfig, Subject } from '../types';
import { portfolioSubject, toSubjectOnly } from '../types';

export const isAllocatorOpenState = atom<boolean>({
  key: 'isAllocatorOpenState',
  default: false,
  effects: [resetOnStudioReset],
});

export const openAllocatorSubject = atom<Subject | undefined>({
  key: 'openAllocatorSubject',
  default: undefined,
  effects: [recoilHistoryEffect, resetOnStudioReset],
});

export const openPrivateAllocatorPortfolioAtom = atom<PrivatePortfolioNode | undefined>({
  key: 'openPrivateAllocatorPortfolioAtom',
  default: undefined,
  effects: [recoilHistoryEffect, resetOnStudioReset],
});

export const openPrivateAllocatorConfig = atom<PrivateAllocatorConfig | undefined>({
  key: 'openPrivateAllocatorConfig',
  default: undefined,
  effects: [resetOnStudioReset],
});

export const openPrivateAllocatorPortfolio = selector<PrivatePortfolioNode | undefined>({
  key: 'openPrivateAllocatorPortfolio',
  get: ({ get }) => {
    return get(openPrivateAllocatorPortfolioAtom);
  },
  set: ({ set, reset }, value) => {
    set(openPrivateAllocatorPortfolioAtom, value);
    if (value instanceof DefaultValue) {
      reset(openPrivateAllocatorPortfolioAtom);
      reset(openPrivateAllocatorConfig);
      return;
    }

    set(openPrivateAllocatorPortfolioAtom, value);
    set(openPrivateAllocatorConfig, value?.id ? { privatePortfolioId: value.id } : undefined);
  },
});

export const isAnyAllocatorOpen = selector<boolean>({
  key: 'isAnyAllocatorOpen',
  get: ({ get }) => {
    return get(isAllocatorOpenState) || !isNil(get(openAllocatorSubject)) || !isNil(get(openPrivateAllocatorPortfolio));
  },
});

// Contains the part of the Allocator settings that get saved in the analysis view
export const openAllocatorSubjectConfig = atom<AllocatorConfig | undefined>({
  key: 'openAllocatorSubjectConfig',
  default: undefined,
  effects: [resetOnStudioReset],
});

const allocatorAnalysisSubjectInternal = atomFamily<AnalysisSubject | undefined, Subject | undefined>({
  key: 'allocatorAnalysisSubjectInternal',
  default: undefined,
  effects: [recoilHistoryEffect, resetOnStudioReset],
});

export const allocatorAnalysisSubject = selectorFamily<AnalysisSubject | undefined, Subject | undefined>({
  key: 'allocatorAnalysisSubject',
  get:
    (subject) =>
    ({ get }) => {
      return get(allocatorAnalysisSubjectInternal(toSubjectOnly(subject)));
    },
  set:
    (subject) =>
    ({ set, reset }, value) => {
      const internal = allocatorAnalysisSubjectInternal(toSubjectOnly(subject));
      if (value instanceof DefaultValue) {
        reset(internal);
        return;
      }
      set(internal, value);
    },
});

/**
 * Live in-Studio override of each Live Aggregated Portfolio's organizeOption (Group by), keyed by portfolio id.
 * Held as one map atom (not an atomFamily) so it round-trips to/from the saved view as a single unit, matching
 * viewTaxRatesPerSubject; aggregatedGroupingState below is the per-portfolio accessor callers use.
 */
export const viewAggregatedGroupings = atom<Record<string, CombinePortfoliosOrganizeOptionEnum>>({
  key: 'viewAggregatedGroupings',
  default: {},
  effects: [recoilHistoryEffect, resetOnStudioReset],
});

/** Per-portfolio accessor over viewAggregatedGroupings; undefined = use the create-time organizeOption. */
export const aggregatedGroupingState = selectorFamily<CombinePortfoliosOrganizeOptionEnum | undefined, number>({
  key: 'aggregatedGroupingState',
  get:
    (portfolioId) =>
    ({ get }) =>
      get(viewAggregatedGroupings)[String(portfolioId)],
  set:
    (portfolioId) =>
    ({ set }, value) => {
      set(viewAggregatedGroupings, (current) => {
        const next = { ...current };
        if (value instanceof DefaultValue || value === undefined) {
          delete next[String(portfolioId)];
        } else {
          next[String(portfolioId)] = value;
        }
        return next;
      });
    },
});

export const secondarySubject = selectorFamily<Subject | undefined, Subject>({
  key: 'secondarySubject',
  get:
    (subject) =>
    ({ get }) => {
      const allocatorSubject = get(allocatorAnalysisSubject(subject));
      if (allocatorSubject?.secondaryPortfolio) {
        return portfolioSubject(allocatorSubject.secondaryPortfolio.id, allocatorSubject.secondaryPortfolio.version);
      }
      return undefined;
    },
});

export const compareColumnOpen = atom<boolean>({
  key: 'compareColumnOpen',
  default: false,
  effects: [resetOnStudioReset],
});

export const hasAllocationPercentError = atom<boolean>({
  key: 'hasAllocationPercentError',
  default: false,
});
